import React, { useState, useEffect } from 'react';
import {
    Loader2, AlertTriangle, RefreshCw, FileText, Users, Calendar, Shield, Search,
    CheckCircle, XCircle, ChevronDown, ChevronUp, Plus, Edit2, Trash2, ArrowRight
} from 'lucide-react';
import { campusIntelligence, officialDocuments } from '../../services/api';

// ─── Reusable UI ─────────────────────────────────────────────────────────────

const SectionHeader = ({ title, icon: Icon, color = '#6366f1', count }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 14, paddingBottom: 8,
        borderBottom: `2px solid ${color}20`,
    }}>
        {Icon && <Icon size={16} style={{ color }} />}
        <h3 style={{ fontSize: 15, fontWeight: 600, color: '#111827', margin: 0 }}>{title}</h3>
        {count != null && (
            <span style={{
                fontSize: 11, fontWeight: 600, color, background: `${color}14`,
                padding: '2px 8px', borderRadius: 6,
            }}>
                {count}
            </span>
        )}
    </div>
);

const Badge = ({ label, color }) => (
    <span style={{
        fontSize: 10, fontWeight: 700, color: color, background: `${color}14`,
        padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', whiteSpace: 'nowrap'
    }}>
        {label}
    </span>
);

const StatusBadge = ({ approved }) => {
    return approved ? (
        <Badge label="Approved" color="#10b981" />
    ) : (
        <Badge label="Needs Review" color="#f59e0b" />
    );
};

const ScopeBadge = ({ scope, campus_id }) => {
    const isDistrict = scope === 'district' || !campus_id;
    return <Badge label={isDistrict ? 'District Default' : 'Campus Override'} color={isDistrict ? '#8b5cf6' : '#3b82f6'} />;
};


// ─── Modal Form Helper ───────────────────────────────────────────────────────

const EditModal = ({ title, fields, initialData, onClose, onSave }) => {
    const [data, setData] = useState(initialData || {});
    const [saving, setSaving] = useState(false);

    const handleChange = (k, v) => setData(d => ({ ...d, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        const submitData = { ...data };
        for (const f of fields) {
            if (f.type === 'json' && typeof submitData[f.key] === 'string') {
                try {
                    submitData[f.key] = JSON.parse(submitData[f.key]);
                } catch {
                    alert(`Invalid JSON in field: ${f.label}`);
                    return;
                }
            }
        }

        setSaving(true);
        try {
            await onSave(submitData);
            onClose();
        } catch (err) {
            alert("Error saving: " + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            <form onSubmit={handleSubmit} style={{
                background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500,
                maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}>
                <h3 style={{ marginTop: 0, marginBottom: 16 }}>{title}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                    {fields.map(f => (
                        <div key={f.key}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' }}>{f.label}</label>
                            {f.type === 'textarea' ? (
                                <textarea value={data[f.key] || ''} onChange={ev => handleChange(f.key, ev.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, minHeight: 80, fontFamily: 'inherit' }} />
                            ) : f.type === 'json' ? (
                                <textarea value={typeof data[f.key] === 'string' ? data[f.key] : JSON.stringify(data[f.key] || [], null, 2)} onChange={ev => {
                                    try {
                                        const parsed = JSON.parse(ev.target.value);
                                        handleChange(f.key, parsed);
                                    } catch {
                                        handleChange(f.key, ev.target.value);
                                    }
                                }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, minHeight: 120, fontFamily: 'monospace' }} />
                            ) : (
                                <input type={f.type || 'text'} value={data[f.key] || ''} onChange={ev => handleChange(f.key, f.type === 'checkbox' ? ev.target.checked : ev.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }} />
                            )}
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} style={{ background: '#f3f4f6', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
                    <button type="submit" disabled={saving} style={{ background: '#6366f1', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
};


// ─── Helper: read CI flags from localStorage (loaded at app mount) ────────────

function getCIFlag(flagKey) {
    try {
        const flags = JSON.parse(localStorage.getItem('saige_feature_flags') || '{}');
        // Backend strips "ENABLE_" prefix and lowercases: e.g. campus_intelligence_promotion
        return flags[flagKey] === true;
    } catch {
        return false;
    }
}


// ─── Sections ───────────────────────────────────────────────────────────────

const DocumentsSection = () => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const res = await officialDocuments.list();

            // Filter to only CI-relevant documents
            const ciTypes = [
                'student_handbook', 'staff_handbook', 'bell_schedule',
                'half_day_schedule', 'campus_procedures', 'emergency_procedures',
                'discipline_matrix', 'student_support_protocols', 'parent_contact_protocols',
                'referral_process', 'intervention_tiers', 'academic_calendar',
                'pd_calendar', 'pd_deadlines', 'testing_dates', 'staff_directory'
            ];

            const relevantDocs = (res.data.documents || []).filter(d => ciTypes.includes(d.doc_type));
            setDocs(relevantDocs);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleExtractText = async (id) => {
        try {
            await campusIntelligence.extractText(id);
            alert("Extraction queued or completed.");
            load();
        } catch (err) {
            alert("Error extracting text: " + (err.response?.data?.detail || err.message));
        }
    };

    const handleStructureExtract = async (id) => {
        try {
            await campusIntelligence.triggerExtraction(id);
            alert("Structured extraction initiated.");
        } catch (err) {
            alert("Error running structured extraction: " + (err.response?.data?.detail || err.message));
        }
    };

    if (loading) return <div>Loading documents...</div>;

    return (
        <div>
            <SectionHeader title="Source Documents" icon={FileText} count={docs.length} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {docs.map(doc => (
                    <div key={doc.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>{doc.title}</div>
                            <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <span>Type: {doc.doc_type}</span>
                                <span>Scope: {doc.scope}</span>
                                <span>Version: {doc.version}</span>
                                <span>Active: {doc.active ? 'Yes' : 'No'}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleExtractText(doc.id)} style={{ fontSize: 12, padding: '6px 10px', cursor: 'pointer', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 4 }}>Extract Text</button>
                            <button onClick={() => handleStructureExtract(doc.id)} style={{ fontSize: 12, padding: '6px 10px', cursor: 'pointer', background: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe', borderRadius: 4, fontWeight: 500 }}>Run AI Extraction</button>
                        </div>
                    </div>
                ))}
                {docs.length === 0 && <div style={{ fontSize: 13, color: '#6b7280', padding: 16 }}>No official documents found.</div>}
            </div>
        </div>
    );
};

const ContactsSection = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await campusIntelligence.getContacts({ include_unapproved: true });
            setContacts(res.data.contacts || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleApprove = async (id) => {
        if (!confirm('Approve this contact?')) return;
        try {
            await campusIntelligence.approveContact(id);
            load();
        } catch {
            alert('Error approving contact');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Delete this contact?')) return;
        try {
            await campusIntelligence.deleteContact(id);
            load();
        } catch {
            alert('Error deleting contact');
        }
    };

    if (loading) return <div>Loading...</div>;

    const fields = [
        { key: 'name', label: 'Name' },
        { key: 'role', label: 'Role' },
        { key: 'department', label: 'Department' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'extension', label: 'Extension' },
    ];

    return (
        <div>
            {editTarget && <EditModal title={editTarget.id ? "Edit Contact" : "Add Contact"} fields={fields} initialData={editTarget} onClose={() => setEditTarget(null)} onSave={async (d) => { await campusIntelligence.upsertContact(d); load(); }} />}
            <SectionHeader title="Contacts" icon={Users} count={contacts.length} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button onClick={() => setEditTarget({})} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Plus size={14} /> Add Contact
                </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', color: '#475569', fontSize: 12 }}>
                    <tr>
                        <th style={{ padding: '10px 14px' }}>Name / Role</th>
                        <th style={{ padding: '10px 14px' }}>Contact Info</th>
                        <th style={{ padding: '10px 14px' }}>Scope</th>
                        <th style={{ padding: '10px 14px' }}>Status</th>
                        <th style={{ padding: '10px 14px', width: 100 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {contacts.map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{c.name}</div>
                                <div style={{ color: '#6b7280', fontSize: 12 }}>{c.role} {c.department ? `(${c.department})` : ''}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#374151' }}>
                                {c.email && <div>{c.email}</div>}
                                {c.phone && <div>{c.phone} {c.extension ? `Ext: ${c.extension}` : ''}</div>}
                            </td>
                            <td style={{ padding: '10px 14px' }}><ScopeBadge scope={c.scope} campus_id={c.campus_id} /></td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge approved={c.approved} /></td>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {!c.approved && <button onClick={() => handleApprove(c.id)} title="Approve" style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}><CheckCircle size={14}/></button>}
                                    <button onClick={() => setEditTarget(c)} title="Edit" style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}><Edit2 size={14} /></button>
                                    <button onClick={() => handleDelete(c.id)} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14}/></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {contacts.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No contacts found.</td></tr>}
                </tbody>
            </table>
        </div>
    );
};

const SchedulesSection = () => {
    const [events, setEvents] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editEvent, setEditEvent] = useState(null);
    const [editSchedule, setEditSchedule] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [evRes, schRes] = await Promise.all([
                campusIntelligence.getEvents({ include_unapproved: true }),
                campusIntelligence.getSchedules({ include_unapproved: true })
            ]);
            setEvents(evRes.data.events || []);
            setSchedules(schRes.data.schedules || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Read from already-loaded feature flags in localStorage (set at app mount via /api/feature-flags).
    // "ENABLE_CAMPUS_INTELLIGENCE_PROMOTION" is exposed as "campus_intelligence_promotion" by the backend.
    const promotionEnabled = getCIFlag('campus_intelligence_promotion');

    const handlePromoteSchedule = async (id) => {
        if (!confirm('WARNING: This copies the approved schedule into runtime settings. Later edits here will not auto-update settings.\n\nProceed to promote?')) return;
        try {
            await campusIntelligence.promoteSchedule(id, "campus");
            alert('Schedule promoted successfully!');
            load();
        } catch (err) {
            alert('Error promoting schedule: ' + (err.response?.data?.detail || err.message));
        }
    };

    if (loading) return <div>Loading...</div>;

    const eventFields = [
        { key: 'title', label: 'Title' },
        { key: 'event_date', label: 'Date (YYYY-MM-DD)' },
        { key: 'start_time', label: 'Start Time' },
        { key: 'end_time', label: 'End Time' },
        { key: 'category', label: 'Category (event, holiday, pd)' },
    ];

    const scheduleFields = [
        { key: 'name', label: 'Name' },
        { key: 'schedule_type', label: 'Type (regular, early_release, assembly)' },
        { key: 'period_length_minutes', label: 'Period Length (min)' },
        { key: 'periods', label: 'Periods (JSON array of strings)', type: 'json' },
        { key: 'notes', label: 'Notes', type: 'textarea' },
    ];

    return (
        <div>
            {editEvent && <EditModal title={editEvent.id ? "Edit Event" : "Add Event"} fields={eventFields} initialData={editEvent} onClose={() => setEditEvent(null)} onSave={async (d) => { await campusIntelligence.upsertEvent(d); load(); }} />}
            {editSchedule && <EditModal title={editSchedule.id ? "Edit Schedule" : "Add Schedule"} fields={scheduleFields} initialData={editSchedule} onClose={() => setEditSchedule(null)} onSave={async (d) => { await campusIntelligence.upsertSchedule(d); load(); }} />}

            <SectionHeader title="Schedules" icon={Calendar} count={schedules.length} color="#ec4899" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button onClick={() => setEditSchedule({})} style={{ background: '#ec4899', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Plus size={14} /> Add Schedule
                </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginBottom: 40 }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', color: '#475569', fontSize: 12 }}>
                    <tr>
                        <th style={{ padding: '10px 14px' }}>Name / Type</th>
                        <th style={{ padding: '10px 14px' }}>Periods</th>
                        <th style={{ padding: '10px 14px' }}>Scope</th>
                        <th style={{ padding: '10px 14px' }}>Status</th>
                        <th style={{ padding: '10px 14px', width: 140 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {schedules.map(s => (
                        <tr key={s.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{s.name}</div>
                                <div style={{ color: '#6b7280', fontSize: 12 }}>{s.schedule_type}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#374151' }}>
                                {s.periods?.length} periods ({s.period_length_minutes}m)
                            </td>
                            <td style={{ padding: '10px 14px' }}><ScopeBadge scope={s.scope} campus_id={s.campus_id} /></td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge approved={s.approved} /></td>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {!s.approved && <button onClick={async () => { if(confirm('Approve?')) { await campusIntelligence.approveSchedule(s.id); load(); } }} title="Approve" style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}><CheckCircle size={14}/></button>}
                                    <button onClick={() => setEditSchedule(s)} title="Edit" style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}><Edit2 size={14} /></button>
                                    <button onClick={async () => { if(confirm('Delete?')) { await campusIntelligence.deleteSchedule(s.id); load(); } }} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14}/></button>
                                    {s.approved && (
                                        promotionEnabled
                                            ? <button onClick={() => handlePromoteSchedule(s.id)} title="Promote to Settings" style={{ background: '#fef08a', color: '#854d0e', border: '1px solid #fde047', borderRadius: 4, padding: '2px 6px', fontSize: 10, cursor: 'pointer', fontWeight: 600 }}>Promote</button>
                                            : <span title="ENABLE_CAMPUS_INTELLIGENCE_PROMOTION is off in this environment" style={{ background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600, cursor: 'not-allowed' }}>Promote (disabled)</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                    {schedules.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No schedules found.</td></tr>}
                </tbody>
            </table>

            <SectionHeader title="Events" icon={Calendar} count={events.length} color="#8b5cf6" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button onClick={() => setEditEvent({})} style={{ background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Plus size={14} /> Add Event
                </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', color: '#475569', fontSize: 12 }}>
                    <tr>
                        <th style={{ padding: '10px 14px' }}>Title</th>
                        <th style={{ padding: '10px 14px' }}>Date/Time</th>
                        <th style={{ padding: '10px 14px' }}>Scope</th>
                        <th style={{ padding: '10px 14px' }}>Status</th>
                        <th style={{ padding: '10px 14px', width: 100 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map(ev => (
                        <tr key={ev.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{ev.title}</div>
                                <div style={{ color: '#6b7280', fontSize: 12 }}>{ev.category}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#374151' }}>
                                <div>{ev.event_date || 'TBD'}</div>
                                {ev.start_time && <div style={{ fontSize: 12 }}>{ev.start_time} - {ev.end_time}</div>}
                            </td>
                            <td style={{ padding: '10px 14px' }}><ScopeBadge scope={ev.scope} campus_id={ev.campus_id} /></td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge approved={ev.approved} /></td>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {!ev.approved && <button onClick={async () => { if(confirm('Approve?')) { await campusIntelligence.approveEvent(ev.id); load(); } }} title="Approve" style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}><CheckCircle size={14}/></button>}
                                    <button onClick={() => setEditEvent(ev)} title="Edit" style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}><Edit2 size={14} /></button>
                                    <button onClick={async () => { if(confirm('Delete?')) { await campusIntelligence.deleteEvent(ev.id); load(); } }} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14}/></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {events.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No events found.</td></tr>}
                </tbody>
            </table>
        </div>
    );
};

const PoliciesSection = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await campusIntelligence.getPolicies({ include_unapproved: true });
            setPolicies(res.data.policies || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) return <div>Loading...</div>;

    const fields = [
        { key: 'policy_area', label: 'Policy Area (e.g., dress_code, attendance, behavior)' },
        { key: 'infraction_category', label: 'Infraction Category' },
        { key: 'allowed_teacher_actions', label: 'Teacher Actions (JSON array)', type: 'json' },
        { key: 'escalation_steps', label: 'Escalation Steps (JSON array)', type: 'json' },
        { key: 'office_referral_threshold', label: 'Office Referral Threshold' },
        { key: 'documentation_required', label: 'Documentation Required?', type: 'checkbox' },
        { key: 'exceptions_or_notes', label: 'Notes', type: 'textarea' },
    ];

    return (
        <div>
            {editTarget && <EditModal title={editTarget.id ? "Edit Policy" : "Add Policy"} fields={fields} initialData={editTarget} onClose={() => setEditTarget(null)} onSave={async (d) => { await campusIntelligence.upsertPolicy(d); load(); }} />}
            <SectionHeader title="Policies & Procedures" icon={Shield} count={policies.length} color="#14b8a6" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button onClick={() => setEditTarget({})} style={{ background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Plus size={14} /> Add Policy
                </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', color: '#475569', fontSize: 12 }}>
                    <tr>
                        <th style={{ padding: '10px 14px' }}>Policy Area / Infraction</th>
                        <th style={{ padding: '10px 14px' }}>Actions / Escalation</th>
                        <th style={{ padding: '10px 14px' }}>Scope</th>
                        <th style={{ padding: '10px 14px' }}>Status</th>
                        <th style={{ padding: '10px 14px', width: 100 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {policies.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ fontWeight: 600, color: '#111827' }}>{p.policy_area}</div>
                                <div style={{ color: '#6b7280', fontSize: 12 }}>{p.infraction_category}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: '#374151', fontSize: 12 }}>
                                {p.allowed_teacher_actions?.length > 0 && <div>{p.allowed_teacher_actions.length} actions</div>}
                                {p.escalation_steps?.length > 0 && <div>{p.escalation_steps.length} steps</div>}
                                {p.documentation_required && <span style={{ color: '#ef4444', fontWeight: 600 }}>Doc Req</span>}
                            </td>
                            <td style={{ padding: '10px 14px' }}><ScopeBadge scope={p.scope} campus_id={p.campus_id} /></td>
                            <td style={{ padding: '10px 14px' }}><StatusBadge approved={p.approved} /></td>
                            <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    {!p.approved && <button onClick={async () => { if(confirm('Approve?')) { await campusIntelligence.approvePolicy(p.id); load(); } }} title="Approve" style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer' }}><CheckCircle size={14}/></button>}
                                    <button onClick={() => setEditTarget(p)} title="Edit" style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer' }}><Edit2 size={14} /></button>
                                    <button onClick={async () => { if(confirm('Delete?')) { await campusIntelligence.deletePolicy(p.id); load(); } }} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14}/></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {policies.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No policies found.</td></tr>}
                </tbody>
            </table>
        </div>
    );
};

const PreviewSection = () => {
    const [query, setQuery] = useState('');
    const [queryType, setQueryType] = useState('planning');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [includeUnapproved, setIncludeUnapproved] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    // Read from already-loaded feature flags in localStorage.
    // "ENABLE_CAMPUS_INTELLIGENCE_CONTEXT_RESOLUTION" → "campus_intelligence_context_resolution"
    const contextResolutionEnabled = getCIFlag('campus_intelligence_context_resolution');

    const handleTest = async () => {
        setLoading(true);
        setPreviewError(null);
        try {
            const res = await campusIntelligence.getContextPreview({
                query,
                query_type: queryType,
                include_unapproved: includeUnapproved ? 'true' : 'false'
            });
            setResult(res.data);
        } catch (err) {
            if (err.response?.status === 403) {
                setPreviewError('Context resolution is not yet enabled in this environment. The ENABLE_CAMPUS_INTELLIGENCE_CONTEXT_RESOLUTION flag must be turned on to use this feature.');
            } else {
                setPreviewError('Error fetching context preview: ' + (err.response?.data?.detail || err.message));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <SectionHeader title="Context Preview (Admin Only)" icon={Search} color="#f59e0b" />

            {/* Proactive flag-off banner — shown immediately without needing an API call */}
            {!contextResolutionEnabled && (
                <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: '#854d0e', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                        <strong>Context resolution is not yet enabled in this environment.</strong><br />
                        The <code>ENABLE_CAMPUS_INTELLIGENCE_CONTEXT_RESOLUTION</code> flag is currently <strong>off</strong> in production. Enable it in Cloud Run environment variables to activate preview for internal testing.
                    </div>
                </div>
            )}

            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12, marginBottom: 20, fontSize: 13, color: '#92400e' }}>
                <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                <strong>Note:</strong> This simulates what a teacher would receive if they asked a question of the specified type. By default, teachers <em>only</em> see approved records.
            </div>

            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 16, marginBottom: 16 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Sample Input Query</label>
                        <input type="text" value={query} onChange={ev => setQuery(ev.target.value)} placeholder="e.g. 'What is the schedule for Wednesday?'" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Detected Query Type</label>
                        <select value={queryType} onChange={ev => setQueryType(ev.target.value)} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}>
                            <option value="schedule">Schedule</option>
                            <option value="policy">Policy</option>
                            <option value="contacts">Contacts</option>
                            <option value="planning">Planning</option>
                            <option value="general">General</option>
                        </select>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                        <input type="checkbox" checked={includeUnapproved} onChange={ev => setIncludeUnapproved(ev.target.checked)} />
                        Include unapproved records (Admin override)
                    </label>
                    <button onClick={handleTest} disabled={loading} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>
                        {loading ? 'Resolving...' : 'Run Preview'}
                    </button>
                </div>
            </div>

            {previewError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 16, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
                    <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {previewError}
                </div>
            )}

            {result && !previewError && (
                <div style={{ background: '#1e293b', color: '#f8fafc', borderRadius: 12, padding: 20, fontFamily: 'monospace', fontSize: 13, overflowX: 'auto' }}>
                    <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 16, borderBottom: '1px solid #334155', paddingBottom: 8 }}>
                        Resolved Context Payload
                    </div>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};


// ─── Main Component ──────────────────────────────────────────────────────────

const CampusIntelligenceTab = () => {
    const [activeTab, setActiveTab] = useState('documents');

    const tabs = [
        { id: 'documents', label: 'Source Documents', icon: FileText },
        { id: 'contacts', label: 'Contacts', icon: Users },
        { id: 'schedules', label: 'Schedules & Events', icon: Calendar },
        { id: 'policies', label: 'Policies', icon: Shield },
        { id: 'preview', label: 'Context Preview', icon: Search }
    ];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Campus Intelligence</h2>
                    <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Manage operational knowledge, contacts, schedules, and policies.</p>
                </div>
            </div>

            {/* Horizontal Tabs */}
            <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid #e5e7eb', marginBottom: 24, overflowX: 'auto' }}>
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none',
                            padding: '10px 4px', cursor: 'pointer',
                            borderBottom: activeTab === t.id ? '2px solid #6366f1' : '2px solid transparent',
                            color: activeTab === t.id ? '#6366f1' : '#6b7280',
                            fontWeight: activeTab === t.id ? 600 : 500,
                            fontSize: 14, transition: 'all 0.15s', whiteSpace: 'nowrap'
                        }}
                    >
                        <t.icon size={16} />
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'documents' && <DocumentsSection />}
            {activeTab === 'contacts' && <ContactsSection />}
            {activeTab === 'schedules' && <SchedulesSection />}
            {activeTab === 'policies' && <PoliciesSection />}
            {activeTab === 'preview' && <PreviewSection />}
        </div>
    );
};

export default CampusIntelligenceTab;
